import type { ILogTransporter, Level, LogMessage } from 'fixparser-common';
import { uuidv4 } from 'fixparser-common';
import { ConsoleLogTransport, type ConsoleFormat as Format } from 'fixparser-plugin-log-console';

/**
 * Configuration options for logging output.
 *
 * @public
 */
export type LogOptions = {
    name?: string;
    id?: string;
    level?: Level;
    format?: Format;
    transport?: ILogTransporter;
};

/**
 * Determines if a message should be logged based on the current log level.
 * Messages are logged if their level is equal to or more severe than the current level.
 *
 * @param {LogMessage} message - The message to check
 * @param {Level} level - The current log level
 * @returns {boolean} True if the message should be logged, false otherwise
 */
const shouldLog = (message: LogMessage, level: Level): boolean => {
    if (level === 'error' && message.level === 'error') {
        return true;
    }
    if (level === 'warn' && (message.level === 'error' || message.level === 'warn')) {
        return true;
    }
    return level === 'info' && (message.level === 'error' || message.level === 'warn' || message.level === 'info');
};

/**
 * Logger class for handling FIX protocol logging.
 * Provides methods for logging messages at different severity levels
 * and supports custom log transports and formats.
 */
export class Logger {
    /** Name of the logger instance */
    public name: string;
    /** Unique identifier for the logger instance */
    public id: string;
    /** Whether logging is enabled */
    readonly #enabled = true;
    /** The transport used for sending log messages */
    readonly #transport: ILogTransporter;
    /** The format used for log messages */
    readonly #format: Format;
    /** Current log level */
    #level: Level;

    /**
     * Creates a new Logger instance with the specified options.
     *
     * @param {LogOptions} [options] - Configuration options for the logger
     * @param {string} [options.name=''] - Name of the logger
     * @param {string} [options.id] - Unique identifier for the logger (auto-generated if not provided)
     * @param {Level} [options.level='info'] - Initial log level
     * @param {Format} [options.format='json'] - Log message format
     * @param {ILogTransporter} [options.transport] - Custom log transport (defaults to console)
     */
    constructor(options?: LogOptions) {
        this.name = options?.name ?? '';
        this.id = options?.id ?? uuidv4();
        this.#level = options?.level ?? 'info';
        this.#level = options?.level ?? 'info';
        this.#format = options?.format ?? 'json';
        this.#transport = options?.transport ?? new ConsoleLogTransport({ format: this.#format });
    }

    /**
     * Processes a log message, adding metadata and sending it through the transport.
     * Only processes messages if logging is enabled and the message level meets the current level.
     *
     * @param {LogMessage} msg - The message to process
     * @returns {this} The logger instance for chaining
     */
    private processMessage(msg: LogMessage): this {
        if (this.#level === 'silent' || !this.#enabled) return this;

        if (!shouldLog(msg, this.#level)) {
            return this;
        }

        const processedMessage = {
            name: this.name,
            id: this.id,
            timestamp: Date.now(),
            ...msg,
        };

        this.#transport.send(processedMessage);

        return this;
    }

    /**
     * Logs a message at the specified level.
     * If no level is specified, defaults to 'info'.
     *
     * @param {LogMessage} msg - The message to log
     * @returns {this} The logger instance for chaining
     */
    public log(msg: LogMessage): this {
        return this.processMessage({ ...msg, level: msg.level ? msg.level : ('info' as Level) });
    }

    /**
     * Logs a warning message.
     *
     * @param {Omit<LogMessage, 'level'>} msg - The message to log (level will be set to 'warn')
     * @returns {this} The logger instance for chaining
     */
    public logWarning(msg: Omit<LogMessage, 'level'>): this {
        return this.processMessage({ ...msg, level: 'warn' as Level } as LogMessage);
    }

    /**
     * Logs an error message.
     *
     * @param {Omit<LogMessage, 'level'>} msg - The message to log (level will be set to 'error')
     * @returns {this} The logger instance for chaining
     */
    public logError(msg: Omit<LogMessage, 'level'>): this {
        return this.processMessage({ ...msg, level: 'error' as Level } as LogMessage);
    }

    /**
     * Sets whether the logger should be silent.
     * When silent, no messages will be logged regardless of their level.
     *
     * @param {boolean} silent - Whether to silence the logger
     */
    public set silent(silent: boolean) {
        this.#level = silent ? 'silent' : 'info';
    }

    /**
     * Gets whether the logger is currently silent.
     *
     * @returns {boolean} True if the logger is silent, false otherwise
     */
    public get silent(): boolean {
        return this.#level === 'silent';
    }

    /**
     * Gets the current log level.
     *
     * @returns {Level} The current log level
     */
    public get level(): Level {
        return this.#level;
    }

    /**
     * Gets the current log format.
     *
     * @returns {Format} The current log format
     */
    public get format(): Format {
        return this.#format;
    }
}

export type { Format };
